import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import {
	Box,
	Button,
	Card,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';

import { resolvePhoneNumberFormat } from '@shared-components-web';
import { getContacts } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import useFacilityStore from '@src/store/modules/facility.store';

function ContactCard({
	icon,
	title,
	description,
	subTitle,
	phone,
	infoTooltip,
}: Readonly<{
	icon: React.ReactNode;
	title: string;
	description: string;
	subTitle: string;
	phone: string;
	infoTooltip?: string;
}>) {
	return (
		<Card
			sx={{
				p: '20px 12px',
				border: '1px solid',
				borderColor: 'neutrals.primary',
				boxShadow: 'none',
				borderRadius: '0.5rem',
				display: 'flex',
				gap: 2,
				height: { xs: 'auto', md: 228 },
				width: '100%',
			}}
		>
			{icon}
			<Box
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				gap={3}
				sx={{ minWidth: 0, flex: 1, height: '100%' }}
			>
				<Box display="flex" flexDirection="column" gap={1}>
					<Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
						<Typography variant="bodyMediumBold" color="text.primary" data-test-id="contact-title">
							{title}
						</Typography>
						{infoTooltip ? (
							<Tooltip
								title={infoTooltip}
								placement="bottom-end"
								arrow
								data-test-id="info-tooltip"
								enterTouchDelay={0}
								componentsProps={{
									tooltip: {
										sx: {
											color: 'text.secondary',
											bgcolor: 'neutrals.primary',
										},
									},
									arrow: {
										sx: { color: 'neutrals.primary' },
									},
								}}
							>
								<InfoOutlinedIcon sx={{ color: 'text.interactive' }} />
							</Tooltip>
						) : null}
					</Box>
					<Typography variant="lowestLevelBook" color="text.tertiary">
						{subTitle}
					</Typography>
					<Divider />
					<Typography variant="bodySmallBook">{description}</Typography>
				</Box>

				<Box display="flex" justifyContent="flex-start" sx={{ mt: 'auto', pb: 1 }}>
					<Button
						variant={infoTooltip ? 'secondaryDefault' : 'primaryDefault'}
						size="S"
						startIcon={<PhoneIcon sx={{ color: infoTooltip ? 'secondary.main' : 'secondary.lightest' }} />}
						href={`tel:${phone}`}
						data-test-id="phone-button"
						sx={{
							'&:hover:not(:focus)': {
								bgcolor: infoTooltip ? 'secondary.lightest' : 'secondary.main',
							},
						}}
					>
						{resolvePhoneNumberFormat(phone) ?? phone}
					</Button>
				</Box>
			</Box>
		</Card>
	);
}

export default function NeedHelpDialog({
	open,
	onClose,
}: Readonly<{
	open: boolean;
	onClose: () => void;
}>) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const facility = useFacilityStore((state) => state.facility);
	const contacts = getContacts(facility);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			data-test-id="need-help-dialog"
			fullScreen={isMobile}
			sx={{
				[theme.breakpoints.up('md')]: {
					'& .MuiDialog-paper': {
						width: '900px',
						maxWidth: '90vw',
					},
				},
				[theme.breakpoints.down('md')]: {
					'& .MuiDialog-container': {
						alignItems: 'flex-end',
					},
					'& .MuiDialog-paper': {
						maxHeight: '75vh',
						borderRadius: '8px 8px 0 0',
					},
				},
			}}
		>
			<DialogTitle data-test-id="need-help-dialog-title" sx={{ backgroundColor: 'backgrounds.neutral' }}>
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Box display="flex" flexDirection="column">
						<Typography variant="header2">Contact Us</Typography>
						<Typography variant="bodyMediumBook" sx={{ mt: 1 }}>
							If you feel like your pet is in distress, you have options.
						</Typography>
					</Box>
					<Button onClick={onClose} data-test-id="modal-close">
						<CloseIcon sx={{ color: 'text.tertiary' }} />
					</Button>
				</Box>
			</DialogTitle>
			<DialogContent data-test-id="upcoming-appointment" sx={{ p: '20px' }}>
				<Grid container wrap="wrap" spacing={2.5} pt={2} alignItems="stretch">
					{contacts.map(({ icon, title, description, subTitle, phone, infoTooltip }) => (
						<Grid item key={`contact-${title}-${phone}`} xs={12} md={6} sx={{ display: 'flex' }}>
							<ContactCard
								icon={icon}
								title={title}
								description={description}
								phone={phone}
								subTitle={subTitle}
								infoTooltip={infoTooltip}
							/>
						</Grid>
					))}
				</Grid>
			</DialogContent>
		</Dialog>
	);
}
